import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Eye } from "lucide-react";
import { getAppPortalRoot } from "../utils/appPortal";
import { getAppScale } from "../utils/appScale";
import WordListMetaBadge from "./WordListMetaBadge";
import {
  formatRgbLabel,
  normalizeGroupementColor,
  sanitizeGroupementTitleInput,
  validateGroupementTitle,
  capitalizeGroupementTitleFirstLetter,
} from "../utils/groupementTitle";
import { computeGroupementStats, normalizeGroupementWord } from "../utils/groupementStats";
import { GROUPING_DETAIL_PANES } from "../constants/groupingsPreferences";
import ToggleSwitch from "./ToggleSwitch";
import "./GroupingsView.css";
import "./WordListBox.css";
import "./WordEditPanel.css";
import "./RegisterSkeleton.css";

const VISIBLE_WORDS = 15;
const WORD_ROW_HEIGHT = 30;
const GROUPING_WORD_SKELETON_NAME_WIDTHS = [
  58, 82, 64, 96, 70, 88, 60, 104, 68, 90, 74, 98, 62, 84, 108,
];
const DEF_PREVIEW_WIDTH = 300;
const DEF_PREVIEW_HEIGHT = 100;

function GroupingWordDefPreview({
  anchorRef,
  scrollRef,
  definition,
  open,
  onMouseEnter,
  onMouseLeave,
}) {
  const [coords, setCoords] = useState(null);
  const [scrollFades, setScrollFades] = useState({ top: false, bottom: false });
  const listRef = useRef(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor) return;
    // Le portail vit hors du cadre mis à l'échelle : on applique le même facteur
    // à l'aperçu et on positionne avec ses dimensions réellement affichées.
    const scale = getAppScale();
    const width = DEF_PREVIEW_WIDTH * scale;
    const height = DEF_PREVIEW_HEIGHT * scale;
    const rect = anchor.getBoundingClientRect();
    let left = rect.left + rect.width / 2 - width / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    let top = rect.bottom + 6;
    if (top + height > window.innerHeight - 8) {
      top = Math.max(8, rect.top - height - 6);
    }
    setCoords({ top, left, scale });
  }, [anchorRef]);

  const updateFades = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const threshold = 2;
    setScrollFades({
      top: el.scrollTop > threshold,
      bottom: el.scrollTop + el.clientHeight < el.scrollHeight - threshold,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onReposition = () => updatePosition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, definition, updatePosition]);

  useLayoutEffect(() => {
    if (!open || !coords) return undefined;
    const frame = requestAnimationFrame(updateFades);
    return () => cancelAnimationFrame(frame);
  }, [open, coords, definition, updateFades]);

  useEffect(() => {
    if (!open) setCoords(null);
  }, [open]);

  if (!open || !coords) return null;

  const text = String(definition || "").trim() || "Aucune définition.";

  return createPortal(
    <div
      className={[
        "grouping-def-preview",
        scrollFades.top ? "grouping-def-preview--fade-top" : "",
        scrollFades.bottom ? "grouping-def-preview--fade-bottom" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        top: coords.top,
        left: coords.left,
        width: DEF_PREVIEW_WIDTH,
        height: DEF_PREVIEW_HEIGHT,
        transform: coords.scale !== 1 ? `scale(${coords.scale})` : undefined,
        transformOrigin: "top left",
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role="tooltip"
    >
      <div
        ref={(node) => {
          listRef.current = node;
          if (scrollRef) scrollRef.current = node;
        }}
        className="grouping-def-preview__scroll custom-scroll"
        onScroll={updateFades}
      >
        {text}
      </div>
    </div>,
    getAppPortalRoot()
  );
}

function GroupingWordRow({ entry, listDisplayMode }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const hideTimerRef = useRef(null);
  const eyeRef = useRef(null);
  const previewScrollRef = useRef(null);

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const showPreview = () => {
    clearHideTimer();
    setPreviewOpen(true);
  };

  const hidePreviewSoon = () => {
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => setPreviewOpen(false), 120);
  };

  useEffect(() => () => clearHideTimer(), []);

  return (
    <div className="word-list-item grouping-word-row">
      <span className="word-list-item__label">{entry.mot}</span>
      <span className="word-list-item__meta">
        <button
          ref={eyeRef}
          type="button"
          className="grouping-word-row__eye"
          aria-label={`Définition de ${entry.mot}`}
          onMouseEnter={showPreview}
          onMouseLeave={hidePreviewSoon}
          onFocus={showPreview}
          onBlur={hidePreviewSoon}
          onWheel={(event) => {
            const el = previewScrollRef.current;
            if (!el || !previewOpen) return;
            el.scrollTop += event.deltaY;
            event.preventDefault();
          }}
        >
          <Eye size={17} aria-hidden="true" />
        </button>
        <WordListMetaBadge entry={entry} mode={listDisplayMode} />
      </span>
      <GroupingWordDefPreview
        anchorRef={eyeRef}
        scrollRef={previewScrollRef}
        definition={entry.definition}
        open={previewOpen}
        onMouseEnter={showPreview}
        onMouseLeave={hidePreviewSoon}
      />
    </div>
  );
}

const MODIFY_MENU_GAP = 8;
const MODIFY_MENU_MARGIN = 8;

function GroupingModifyMenu({
  onRename,
  onDelete,
  onEmpty,
  onRemove,
  onReassign,
  canEmpty = true,
  canReassign = true,
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const scale = getAppScale();
    const rect = trigger.getBoundingClientRect();
    const visualWidth = Math.round(rect.width);
    const layoutWidth = visualWidth / scale;
    let left = rect.left + rect.width / 2 - visualWidth / 2;
    left = Math.max(
      MODIFY_MENU_MARGIN,
      Math.min(left, window.innerWidth - visualWidth - MODIFY_MENU_MARGIN)
    );

    const top = rect.bottom + MODIFY_MENU_GAP;
    const transform = scale === 1 ? "none" : `scale(${scale})`;

    setCoords((prev) =>
      prev &&
      prev.top === top &&
      prev.left === left &&
      prev.width === layoutWidth &&
      prev.transform === transform
        ? prev
        : { top, left, width: layoutWidth, transform }
    );
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const raf = requestAnimationFrame(updatePosition);
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (listRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const options = [
    { id: "rename", label: "Renommer le groupement", onSelect: onRename },
    { id: "delete", label: "Supprimer le groupement", onSelect: onDelete, danger: true },
    canEmpty
      ? { id: "remove", label: "Retirer des mots du groupement", onSelect: onRemove }
      : null,
    canReassign
      ? { id: "reassign", label: "Réaffecter des mots", onSelect: onReassign }
      : null,
    canEmpty
      ? { id: "empty", label: "Vider le groupement", onSelect: onEmpty }
      : null,
  ].filter(Boolean);

  const handleSelect = (option) => {
    setOpen(false);
    option.onSelect?.();
  };

  return (
    <div className={`word-edit-actions-menu${open ? " word-edit-actions-menu--open" : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className="word-edit-actions-menu__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="word-edit-actions-menu__label">Modifications</span>
        <ChevronDown
          size={16}
          strokeWidth={2}
          className="word-edit-actions-menu__chevron"
          aria-hidden="true"
        />
      </button>
      {open && coords
        ? createPortal(
            <ul
              ref={listRef}
              className="word-edit-actions-menu__list grouping-modify-menu__list"
              role="listbox"
              style={{
                position: "fixed",
                top: `${coords.top}px`,
                left: `${coords.left}px`,
                width: `${coords.width}px`,
                maxWidth: `${coords.width}px`,
                transform: coords.transform,
                transformOrigin: "top left",
              }}
            >
              {options.map((option) => (
                <li key={option.id} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    className={`word-edit-actions-menu__option${
                      option.danger ? " word-edit-actions-menu__option--danger" : ""
                    }`}
                    onClick={() => handleSelect(option)}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>,
            getAppPortalRoot()
          )
        : null}
    </div>
  );
}

function GroupingRenameForm({ grouping, usedColors, onCancel, onSave, onReady }) {
  const inputRef = useRef(null);
  const bottomRef = useRef(null);
  const [title, setTitle] = useState(
    () => capitalizeGroupementTitleFirstLetter(grouping?.title || "")
  );
  const [color, setColor] = useState(grouping?.color || "#6b4226");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useLayoutEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end", inline: "nearest" });
    onReady?.();
  }, [onReady]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus({ preventScroll: true });
      bottomRef.current?.scrollIntoView({ block: "end", inline: "nearest" });
      onReady?.();
    });
    return () => cancelAnimationFrame(frame);
  }, [onReady]);

  const titleError = validateGroupementTitle(title);
  const normalizedColor = normalizeGroupementColor(color);
  const usedNormalized = usedColors
    .map((value) => normalizeGroupementColor(value))
    .filter(Boolean);
  const colorTaken = usedNormalized.includes(normalizedColor);
  const canSubmit = !titleError && Boolean(normalizedColor) && !colorTaken && !saving;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError("");
    try {
      await onSave({
        title: capitalizeGroupementTitleFirstLetter(title),
        color: normalizedColor,
      });
    } catch (err) {
      setError(err.message || "Impossible de modifier ce groupement.");
      setSaving(false);
    }
  };

  return (
    <form className="groupement-rename" onSubmit={handleSubmit}>
      <input
        ref={inputRef}
        className="groupement-create__input"
        type="text"
        value={title}
        onChange={(event) => {
          setTitle(
            capitalizeGroupementTitleFirstLetter(
              sanitizeGroupementTitleInput(event.target.value)
            )
          );
          setError("");
        }}
        maxLength={40}
        autoComplete="off"
        spellCheck="false"
        aria-label="Nouveau nom du groupement thématique"
      />
      {title && titleError ? (
        <p className="groupement-create__hint">{titleError}</p>
      ) : null}

      <div className="groupement-create__color-row groupement-rename__color">
        <input
          type="color"
          className="groupement-create__color-picker"
          value={normalizedColor || "#6b4226"}
          onChange={(event) => {
            setColor(event.target.value);
            setError("");
          }}
          aria-label="Couleur du groupement thématique"
        />
        <span
          className="groupement-create__swatch"
          style={{ backgroundColor: normalizedColor || "#6b4226" }}
          aria-hidden="true"
        />
        <span className="groupement-create__rgb">{formatRgbLabel(normalizedColor)}</span>
      </div>
      {colorTaken ? (
        <p className="groupement-create__hint">
          Cette couleur est déjà utilisée par un autre groupement.
        </p>
      ) : null}
      {error ? <p className="groupement-create__error">{error}</p> : null}

      <div className="groupement-create__actions groupement-rename__actions">
        <button
          type="button"
          className="word-edit-btn word-edit-btn--cancel"
          onClick={onCancel}
          disabled={saving}
        >
          Annuler
        </button>
        <button
          type="submit"
          className="word-edit-btn word-edit-btn--primary"
          disabled={!canSubmit}
        >
          {saving ? "Validation…" : "Valider"}
        </button>
      </div>
      <div ref={bottomRef} className="groupement-rename__scroll-anchor" aria-hidden="true" />
    </form>
  );
}

function GroupingStatsSkeleton() {
  return (
    <section className="grouping-stats grouping-stats--pane" aria-hidden="true">
      <h2 className="grouping-stats__title">
        Statistiques du groupement thématique
      </h2>
      <ul className="grouping-stats__list">
        <li>
          <span>Nombre de mots</span>
          <strong className="grouping-stats__value">
            <span className="groupings-skel-metrics">00</span>
            <span className="register-skel groupings-skel--stat-value" />
          </strong>
        </li>
        <li>
          <span>Maîtrisés</span>
          <strong className="grouping-stats__value">
            <span className="groupings-skel-metrics">00 (00 %)</span>
            <span className="register-skel groupings-skel--stat-value" />
          </strong>
        </li>
        <li>
          <span>À revoir</span>
          <strong className="grouping-stats__value">
            <span className="groupings-skel-metrics">00 (00 %)</span>
            <span className="register-skel groupings-skel--stat-value" />
          </strong>
        </li>
        <li>
          <span>Jamais joués</span>
          <strong className="grouping-stats__value">
            <span className="groupings-skel-metrics">00 (00 %)</span>
            <span className="register-skel groupings-skel--stat-value" />
          </strong>
        </li>
        <li>
          <span>Capture la plus récente</span>
          <strong className="grouping-stats__value">
            <span className="groupings-skel-metrics">00/00/0000</span>
            <span className="register-skel groupings-skel--stat-value" />
          </strong>
        </li>
      </ul>
    </section>
  );
}

function GroupingModifyMenuSkeleton() {
  return (
    <div className="word-edit-actions-menu" aria-hidden="true">
      <div className="word-edit-actions-menu__trigger groupings-skel-modify">
        <span className="word-edit-actions-menu__label">
          <span className="groupings-skel-metrics">Modifications</span>
        </span>
        <ChevronDown
          size={16}
          strokeWidth={2}
          className="word-edit-actions-menu__chevron groupings-skel-metrics"
          aria-hidden="true"
        />
        <span className="register-skel groupings-skel--modify-bar" />
      </div>
    </div>
  );
}

function GroupingWordsListSkeleton({ showColorCode = true }) {
  const frameHeight = VISIBLE_WORDS * WORD_ROW_HEIGHT + 2;

  return (
    <div
      className="word-listbox-shell grouping-words-frame word-listbox-shell--skeleton"
      style={{
        width: "100%",
        height: frameHeight,
        minHeight: frameHeight,
        maxHeight: frameHeight,
      }}
      aria-hidden="true"
    >
      <div
        className={`listbox custom-scroll grouping-words-frame__list${
          showColorCode ? "" : " listbox--no-color-code"
        }`}
      >
        {GROUPING_WORD_SKELETON_NAME_WIDTHS.map((width, index) => (
          <div key={index} className="word-list-item grouping-word-row">
            <span className="word-list-item__label">
              <span
                className="register-skel register-skel--label"
                style={{ width: `${width}px` }}
              />
            </span>
            <span className="word-list-item__meta">
              <span className="grouping-word-row__eye">
                <Eye size={17} className="grouping-skel-eye" aria-hidden="true" />
              </span>
              <span className="word-list-meta-badge register-skel" />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function GroupingDetailSkeleton({
  detailPane,
  onDetailPaneChange,
  showColorCode,
}) {
  const showStats = detailPane === GROUPING_DETAIL_PANES.STATS;

  return (
    <div className="containerDetailMot-shell" aria-busy="true">
      <div className="containerDetailMot grouping-detail-scroll">
        <div className="containerDetailMot__content grouping-detail">
          <div className="grouping-detail__toolbar">
            <ToggleSwitch
              compact
              alwaysUncheckedLook
              className="grouping-detail__pane-switch"
              checked={showStats}
              uncheckedSymbol="M"
              checkedSymbol="S"
              ariaLabel={
                showStats
                  ? "Afficher la liste des mots"
                  : "Afficher les statistiques"
              }
              onChange={(checked) => {
                onDetailPaneChange(
                  checked ? GROUPING_DETAIL_PANES.STATS : GROUPING_DETAIL_PANES.LIST
                );
              }}
            />
          </div>
          {showStats ? (
            <GroupingStatsSkeleton />
          ) : (
            <>
              <GroupingWordsListSkeleton showColorCode={showColorCode} />
              <GroupingModifyMenuSkeleton />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function GroupingStatsSection({ stats }) {
  return (
    <section className="grouping-stats grouping-stats--pane">
      <h2 className="grouping-stats__title">
        Statistiques du groupement thématique
      </h2>
      <ul className="grouping-stats__list">
        <li>
          <span>Nombre de mots</span>
          <strong>{stats.total.toLocaleString("fr-FR")}</strong>
        </li>
        <li>
          <span>Maîtrisés</span>
          <strong>
            {stats.mastered.count.toLocaleString("fr-FR")}{" "}
            ({stats.mastered.percentLabel})
          </strong>
        </li>
        <li>
          <span>À revoir</span>
          <strong>
            {stats.review.count.toLocaleString("fr-FR")}{" "}
            ({stats.review.percentLabel})
          </strong>
        </li>
        <li>
          <span>Jamais joués</span>
          <strong>
            {stats.never.count.toLocaleString("fr-FR")}{" "}
            ({stats.never.percentLabel})
          </strong>
        </li>
        <li>
          <span>Capture la plus récente</span>
          <strong>{stats.latestCapture || "—"}</strong>
        </li>
      </ul>
    </section>
  );
}

export default function GroupingDetailPanel({
  grouping = null,
  words = [],
  listDisplayMode,
  showColorCode = true,
  usedColors = [],
  detailPane = GROUPING_DETAIL_PANES.LIST,
  onDetailPaneChange = () => {},
  onRename = async () => {},
  onRequestDelete = () => {},
  onRequestEmpty = () => {},
  onRequestRemoveWords = () => {},
  onRequestReassignWords = () => {},
  canReassign = false,
  loading = false,
}) {
  const listRef = useRef(null);
  const panelScrollRef = useRef(null);
  const [scrollFades, setScrollFades] = useState({ top: false, bottom: false });
  const [renaming, setRenaming] = useState(false);

  const entries = useMemo(
    () =>
      words
        .map(normalizeGroupementWord)
        .filter(Boolean)
        .sort((a, b) =>
          String(a.mot).localeCompare(String(b.mot), "fr", { sensitivity: "base" })
        ),
    [words]
  );

  const stats = useMemo(() => computeGroupementStats(entries), [entries]);

  const updateScrollFades = useCallback(() => {
    const el = listRef.current;
    if (!el) {
      setScrollFades({ top: false, bottom: false });
      return;
    }
    const threshold = 2;
    setScrollFades({
      top: el.scrollTop > threshold,
      bottom: el.scrollTop + el.clientHeight < el.scrollHeight - threshold,
    });
  }, []);

  const scrollPanelToBottom = useCallback(() => {
    const el = panelScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    setRenaming(false);
  }, [grouping?.groupementId]);

  useEffect(() => {
    if (detailPane === GROUPING_DETAIL_PANES.STATS) setRenaming(false);
  }, [detailPane]);

  useLayoutEffect(() => {
    updateScrollFades();
  }, [entries, updateScrollFades]);

  useLayoutEffect(() => {
    if (!renaming) {
      const el = panelScrollRef.current;
      if (el) el.scrollTop = 0;
      return undefined;
    }
    scrollPanelToBottom();
    let innerFrame = 0;
    const frame = requestAnimationFrame(() => {
      scrollPanelToBottom();
      innerFrame = requestAnimationFrame(scrollPanelToBottom);
    });
    const timeout = window.setTimeout(scrollPanelToBottom, 80);
    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(innerFrame);
      window.clearTimeout(timeout);
    };
  }, [renaming, scrollPanelToBottom]);

  if (loading) {
    return (
      <GroupingDetailSkeleton
        detailPane={detailPane}
        onDetailPaneChange={onDetailPaneChange}
        showColorCode={showColorCode}
      />
    );
  }

  if (!grouping) {
    return (
      <div className="containerDetailMot-shell">
        <div className="containerDetailMot grouping-detail-scroll">
          <p className="groupings-detail-empty">
            Sélectionnez un groupement thématique pour voir ses mots.
          </p>
        </div>
      </div>
    );
  }

  const showStats = detailPane === GROUPING_DETAIL_PANES.STATS;
  const frameHeight = VISIBLE_WORDS * WORD_ROW_HEIGHT + 2;

  return (
    <div className="containerDetailMot-shell">
      <div
        ref={panelScrollRef}
        className={`containerDetailMot grouping-detail-scroll${
          renaming ? " grouping-detail-scroll--renaming detail-scroll" : ""
        }`}
      >
        <div className="containerDetailMot__content grouping-detail">
          <div className="grouping-detail__toolbar">
            <ToggleSwitch
              compact
              alwaysUncheckedLook
              className="grouping-detail__pane-switch"
              checked={showStats}
              uncheckedSymbol="M"
              checkedSymbol="S"
              ariaLabel={
                showStats
                  ? "Afficher la liste des mots"
                  : "Afficher les statistiques"
              }
              onChange={(checked) => {
                onDetailPaneChange(
                  checked ? GROUPING_DETAIL_PANES.STATS : GROUPING_DETAIL_PANES.LIST
                );
              }}
            />
          </div>

          {showStats ? (
            <GroupingStatsSection stats={stats} />
          ) : (
            <>
              {entries.length === 0 ? (
                <p className="groupings-detail-empty">
                  Il n&apos;y a actuellement aucun mot affilié à ce groupement
                  thématique.
                </p>
              ) : (
                <div
                  className={[
                    "word-listbox-shell grouping-words-frame",
                    scrollFades.top ? "word-listbox-shell--fade-top" : "",
                    scrollFades.bottom ? "word-listbox-shell--fade-bottom" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={{
                    width: "100%",
                    height: frameHeight,
                    minHeight: frameHeight,
                    maxHeight: frameHeight,
                  }}
                >
                  <div
                    ref={listRef}
                    className={`listbox custom-scroll grouping-words-frame__list${
                      showColorCode ? "" : " listbox--no-color-code"
                    }${entries.length > VISIBLE_WORDS ? " grouping-words-frame__list--overflow" : ""}`}
                    onScroll={updateScrollFades}
                    aria-label={`Mots de ${grouping.title}`}
                  >
                    {entries.map((entry) => (
                      <GroupingWordRow
                        key={entry.mot}
                        entry={entry}
                        listDisplayMode={listDisplayMode}
                      />
                    ))}
                  </div>
                </div>
              )}

              {renaming ? (
                <GroupingRenameForm
                  grouping={grouping}
                  usedColors={usedColors}
                  onReady={scrollPanelToBottom}
                  onCancel={() => setRenaming(false)}
                  onSave={async (payload) => {
                    await onRename(payload);
                    setRenaming(false);
                  }}
                />
              ) : (
                <GroupingModifyMenu
                  onRename={() => setRenaming(true)}
                  onDelete={onRequestDelete}
                  onEmpty={onRequestEmpty}
                  onRemove={onRequestRemoveWords}
                  onReassign={onRequestReassignWords}
                  canEmpty={entries.length > 0}
                  canReassign={canReassign && entries.length > 0}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
